import React, {ReactNode, RefObject} from "react";
import PortalDocument, {PortalDocumentInterface} from "../../document/PortalDocument";
import {Button} from "primereact/button";
import Resizeable, {ResizeableDirection} from "../../../shared/components/Resizeable";
import DocumentSettings from "../../document/DocumentSettings";
import LengthUnit from "../../../shared/units/LengthUnit";
import SidebarRightMode from "../settings/SidebarRightMode";
import {ViewDialogDefinition} from "../../dialog/components/document/ViewDialog";
import DocumentInfo from "../../document/components/DocumentInfo";
import {EncryptDialogDefinition} from "../../dialog/components/security/EncryptDialog";
import DocumentSecurity from "../../document/components/pdf/DocumentSecurity";
import AnnotationList from "../../document/components/pdf/AnnotationList";
import HistoryList from "../../document/components/HistoryList";
import {DecryptDialogDefinition} from "../../dialog/components/security/DecryptDialog";
import {PortalDocumentManagerGlobals} from "../../document/PortalDocumentManager";
import {I18nGlobals} from "../../../shared/translation/i18nInterface";
import Observer from "../../../shared/class/Observer";
import LocationEvent from "../LocationEvent";
import {DescriptionDialogDefinition} from "../../dialog/components/document/DescriptionDialog";
import MenuButton from "../../../shared/menu/MenuButton";
import {HistoryEntry, Information} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import Immutable from "../../../shared/immutable/Immutable";
import Translate from "../../../shared/translation/Translate";
import SidebarFunctions, {SidebarFunctionGlobals} from "../../document/components/pdf/SidebarFunctions";
import {PortalGlobals} from "../../Portal";
import {DebugGlobals} from "../../dialog/components/settings/DebugDialog";
import FunctionHelper from "../../../shared/helper/FunctionHelper";
import {HelperGlobals} from "../../../shared/components/Helper";
import {FormLayerGlobals} from "../pdf/form/FormLayerInterface";
import PortalPureComponent from "../../../shared/components/PortalPureComponent";

export interface RightSidebarProperties extends PortalDocumentManagerGlobals, I18nGlobals, SidebarFunctionGlobals,
	PortalGlobals, DebugGlobals, HelperGlobals, FormLayerGlobals {
	viewerScreen: RefObject<HTMLDivElement>;
	portalDocument?: Immutable<PortalDocumentInterface>;
	locationObserver?: Observer<LocationEvent>;
}

export interface RightSidebarState {
	sidebarRightWidth: string;
}

export class RightSidebarDefinition extends PortalPureComponent<RightSidebarProperties, RightSidebarState> {
	/**
	 * Left sidebar component constructor
	 */
	constructor(props: Readonly<RightSidebarProperties>) {
		super(props);

		this.state = {
			sidebarRightWidth: 350 + LengthUnit.PIXEL
		};

		this.changeRightSidebarMode = this.changeRightSidebarMode.bind(this);
	}

	/**
	 * Change the sidebar right mode to global settings
	 */
	private changeRightSidebarMode(mode: SidebarRightMode): void {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument!.get().id!
		);
		let documentSettings: DocumentSettings = portalDocument.getSettings();
		documentSettings.sidebarRightMode = documentSettings.sidebarRightMode === mode ? SidebarRightMode.NONE : mode;
		this.props.context.documentManager.updateGlobalDocumentList();
	}

	/**
	 * render mouse function
	 */
	private renderMouseFunction(visible: boolean): ReactNode {
		if (!visible) {
			return null;
		}

		return (
			<div className="body">
				<div className="toolbar"/>
				<div className="sidebar-functions is-visible">
					<SidebarFunctions {...this.props}/>
				</div>
			</div>
		);
	}

	/**
	 * render document information
	 */
	private renderDocumentInfo(visible: boolean): ReactNode {
		let portalInfo: Information = this.props.context.info;

		return (
			<div className="body" style={visible ? {} : {display: "none"}} data-help="global.sidebar.info"
				 data-position="left">
				<div className="toolbar">
					{(!this.props.documentChecks || DescriptionDialogDefinition.isEnabled(portalInfo)) &&
						<Button icon="icon icon-list" className="document-menu-info-description"
								tooltip={Translate.value("description.tooltip", {ns: "dialog"})}
								disabled={
									this.props.documentChecks &&
									!DescriptionDialogDefinition.isActive(
										this.props.context.documentManager.getSelection(), portalInfo,
										this.props.signatureEditProfile
									)
								}
								onClick={async () => {
									let command: () => Promise<void> = async () => {
										this.props.context.getPage().setState({
											showDescriptionDialog: true,
											descriptionParameter: undefined
										})
									}

									if (DocumentSettings.checkActiveMouseFunction(this.props)) {
										DocumentSettings.confirmResetMouseFunction(this.props, command);
										return;
									}

									await command();
								}}/>
					}
					{(!this.props.documentChecks || ViewDialogDefinition.isEnabled(portalInfo)) &&
						<Button icon="icon icon-annotation-star" className="document-menu-info-view"
								tooltip={Translate.value("view.tooltip", {ns: "dialog"})}
								disabled={
									this.props.documentChecks &&
									!ViewDialogDefinition.isActive(
										this.props.context.documentManager.getSelection(), portalInfo,
										this.props.signatureEditProfile
									)
								}
								onClick={async () => {
									let command: () => Promise<void> = async () => {
										this.props.context.getPage().setState({
											showViewDialog: true,
											viewParameter: undefined
										})
									}

									if (DocumentSettings.checkActiveMouseFunction(this.props)) {
										DocumentSettings.confirmResetMouseFunction(this.props, command);
										return;
									}

									await command();
								}}/>
					}
				</div>
				<div className="document-info">
					<DocumentInfo {...this.props}/>
				</div>
				<div className="toolbar inverted"/>
			</div>
		);
	}

	/**
	 * render document security information
	 */
	private renderDocumentSecurity(visible: boolean): ReactNode {
		let portalInfo: Information = this.props.context.info;

		return (
			<>
				<div className="body" style={visible ? {} : {display: "none"}} data-help="global.sidebar.security"
					 data-position="left">
					<div className="toolbar">
						{(!this.props.documentChecks || EncryptDialogDefinition.isEnabled(portalInfo)) &&
							<MenuButton {...this.props} icon="icon icon-locked"
										tooltip={Translate.value("encrypt.tooltip", {ns: "dialog"})}
										className="document-menu-security-encrypt"
										disabled={
											this.props.documentChecks &&
											!EncryptDialogDefinition.isActive(
												this.props.context.documentManager.getSelection(), portalInfo,
												this.props.signatureEditProfile
											)
										}
										menuItems={
											[
												{
													label: Translate.value(
														"security.password", {ns: "menu"}
													),
													className: "document-menu-security-encrypt-password",
													icon: "icon icon-shield",
													command: async () => {
														let command: () => Promise<void> = async () => {
															this.props.context.getPage().setState({
																showEncryptDialog: true,
																encryptParameter: undefined
															});
														}

														if (DocumentSettings.checkActiveMouseFunction(this.props)) {
															DocumentSettings.confirmResetMouseFunction(this.props, command);
															return;
														}

														await command();
													}
												},
												{
													label: Translate.value(
														"security.certificate", {ns: "menu"}
													),
													className: "document-menu-security-encrypt-certificate",
													icon: "icon icon-certificate",
													command: async () => {
														let command: () => Promise<void> = async () => {
															this.props.context.getPage().setState({
																showCertificateEncryptDialog: true,
																certificateEncryptParameter: undefined
															});
														}

														if (DocumentSettings.checkActiveMouseFunction(this.props)) {
															DocumentSettings.confirmResetMouseFunction(this.props, command);
															return;
														}

														await command();
													}
												}
											]
										}/>
						}
						{(!this.props.documentChecks || DecryptDialogDefinition.isEnabled(portalInfo)) &&
							<Button icon="icon icon-unlocked" className="document-menu-security-decrypt"
									tooltip={Translate.value("decrypt.tooltip", {ns: "dialog"})}
									disabled={
										this.props.documentChecks &&
										!DecryptDialogDefinition.isActive(
											this.props.context.documentManager.getSelection(), portalInfo,
											this.props.signatureEditProfile
										)
									}
									onClick={async () => {
										let command: () => Promise<void> = async () => {
											this.props.context.getPage().setState({
												showDecryptDialog: true,
												decryptParameter: undefined
											});
										}

										if (DocumentSettings.checkActiveMouseFunction(this.props)) {
											DocumentSettings.confirmResetMouseFunction(this.props, command);
											return;
										}

										await command();
									}}/>
						}
					</div>
					<div className="document-security">
						<DocumentSecurity {...this.props}/>
					</div>
					<div className="toolbar inverted"/>
				</div>
			</>
		);
	}

	/**
	 * render history list
	 */
	private renderHistory(visible: boolean): ReactNode {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return null;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument!.get().id!
		);

		try {
			let activeHistory: HistoryEntry = portalDocument.activeHistory();
			let hasNext: boolean = activeHistory.id! < portalDocument.getHistorySize();
			let hasPrevious: boolean = activeHistory.id! > 1;

			return (
				<div className="body" style={visible ? {} : {display: "none"}} data-help="global.sidebar.history"
					 data-position="left">
					<div className="toolbar">
						<Button disabled={!hasPrevious || portalDocument.isLocked()} icon="icon icon-back"
								onClick={async () => {
									if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
										return;
									}

									let command: () => Promise<void> = async () => {
										let activeHistory: HistoryEntry = portalDocument.activeHistory();
										await this.props.context.documentManager.activateHistory(
											this.props.portalDocument!.get().id!, activeHistory.id! - 1
										);
									}

									if (DocumentSettings.checkActiveMouseFunction(this.props)) {
										DocumentSettings.confirmResetMouseFunction(this.props, command);
										return;
									}

									await command();
								}}/>
						<Button disabled={!hasNext || portalDocument.isLocked()} icon="icon icon-forward"
								onClick={async () => {
									if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
										return;
									}

									let command: () => Promise<void> = async () => {
										let activeHistory: HistoryEntry = portalDocument.activeHistory();
										await this.props.context.documentManager.activateHistory(
											this.props.portalDocument!.get().id!, activeHistory.id! + 1
										);
									}

									if (DocumentSettings.checkActiveMouseFunction(this.props)) {
										DocumentSettings.confirmResetMouseFunction(this.props, command);
										return;
									}

									await command();
								}}/>
					</div>
					<div className="document-history">
						<HistoryList {...this.props}/>
					</div>
					<div className="toolbar inverted"/>
				</div>
			);
		} catch (e) {
			return null;
		}
	}

	/**
	 * render component as react node
	 */
	public render(): ReactNode {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return null;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument!.get().id!
		);
		let documentSettings: DocumentSettings = portalDocument.getSettings();
		let classList: Array<string> = ["sidebar", "right"];

		if (documentSettings.sidebarRightMode !== SidebarRightMode.NONE || documentSettings.showSidebarFunction) {
			classList.push("in");
		}

		return (
			<>
				<div style={{width: this.state.sidebarRightWidth}} className={classList.join(" ")}>
					<Resizeable direction={ResizeableDirection.HORIZONTAL} container={this.props.viewerScreen}
								invert={true} resizeCallback={
						(options: { width: string }) => {
							this.setState({
								sidebarRightWidth: options.width
							});
						}
					}/>
					<Button className="toggle-btn" icon="icon icon-angle-right"
							onClick={() => this.changeRightSidebarMode(SidebarRightMode.NONE)}/>
					{this.renderMouseFunction(documentSettings.showSidebarFunction)}
					{this.renderDocumentInfo(documentSettings.sidebarRightMode === SidebarRightMode.INFO)}
					{this.renderDocumentSecurity(documentSettings.sidebarRightMode === SidebarRightMode.SECURITY)}
					<AnnotationList {...this.props}
									visible={documentSettings.sidebarRightMode === SidebarRightMode.ANNOTATION}/>
					{this.renderHistory(documentSettings.sidebarRightMode === SidebarRightMode.HISTORY)}
				</div>
			</>
		);
	}
}

const RightSidebar = React.memo(RightSidebarDefinition,
	(prevProps: Readonly<RightSidebarProperties>, nextProps: Readonly<RightSidebarProperties>) => {
		if (FunctionHelper.exists(nextProps.portalDocument) &&
			!nextProps.portalDocument!.equals(prevProps.portalDocument)) {
			return false;
		}

		if (nextProps.language !== prevProps.language) {
			return false;
		}

		if (nextProps.showAnnotationLineFunction !== prevProps.showAnnotationLineFunction) {
			return false;
		}

		if (nextProps.showAnnotationCircleFunction !== prevProps.showAnnotationCircleFunction) {
			return false;
		}

		if (nextProps.showAnnotationSquareFunction !== prevProps.showAnnotationSquareFunction) {
			return false;
		}

		if (nextProps.showAnnotationMarkFunction !== prevProps.showAnnotationMarkFunction) {
			return false;
		}

		if (nextProps.showAnnotationUnderlineFunction !== prevProps.showAnnotationUnderlineFunction) {
			return false;
		}

		if (nextProps.showAnnotationSquigglyFunction !== prevProps.showAnnotationSquigglyFunction) {
			return false;
		}

		if (nextProps.showAnnotationStrikeFunction !== prevProps.showAnnotationStrikeFunction) {
			return false;
		}

		if (nextProps.showAnnotationNoteFunction !== prevProps.showAnnotationNoteFunction) {
			return false;
		}

		if (nextProps.showRedactAreaFunction !== prevProps.showRedactAreaFunction) {
			return false;
		}

		if (nextProps.showRedactTextFunction !== prevProps.showRedactTextFunction) {
			return false;
		}

		if (nextProps.showAnnotationReplyFunction !== prevProps.showAnnotationReplyFunction) {
			return false;
		}

		if (nextProps.showAnnotationStampFunction !== prevProps.showAnnotationStampFunction) {
			return false;
		}

		if (nextProps.signatureEditProfile !== prevProps.signatureEditProfile) {
			return false;
		}

		if (nextProps.updateSidebarFunction !== prevProps.updateSidebarFunction) {
			return false;
		}

		if (FunctionHelper.exists(nextProps.formValues) && !nextProps.formValues!.equals(prevProps.formValues)) {
			return false;
		}

		return true;
	}
);

export default RightSidebar;